import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Home, MapPin, Maximize, IndianRupee, BedDouble, ArrowRight, Filter, Search } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import api from "../lib/api";
import { toast } from 'sonner';
import { businessInfo } from '../mock';


const PropertyListing = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // URL-driven listingType filter: 'sale', 'rent', or 'all' (default)
  const listingTypeFromUrl = searchParams.get('listingType');
  const listingTypeFilter = listingTypeFromUrl === 'sale' || listingTypeFromUrl === 'rent'
    ? listingTypeFromUrl
    : 'all';

  useEffect(() => {
    document.title = 'Properties in Greater Noida | Buy, Rent Flats & Plots - CHIV Properties';
    fetchProperties(listingTypeFilter);
  }, [listingTypeFilter]);

  const fetchProperties = async (listingType) => {
    try {
      setLoading(true);
      const params = {};
      if (listingType && listingType !== 'all') params.listingType = listingType;
      const response = await api.get(`/properties`, { params });
      setProperties(response.data.items || []);
    } catch (error) {
      console.error('Error fetching properties:', error);
      toast.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  const setListingType = (value) => {
    // 'all' clears the URL param; others set it
    const next = new URLSearchParams(searchParams);
    if (value === 'all') next.delete('listingType');
    else next.set('listingType', value);
    setSearchParams(next, { replace: true });
  };

  const filteredProperties = properties.filter(property => {
    const matchesSearch =
      property.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      property.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
      property.propertyType.toLowerCase().includes(searchTerm.toLowerCase()) ||
      property.bhk?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || property.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const statusConfig = {
      available: { color: 'bg-green-100 text-green-800', label: 'Available' },
      sold: { color: 'bg-red-100 text-red-800', label: 'Sold' },
      under_construction: { color: 'bg-yellow-100 text-yellow-800', label: 'Under Construction' }
    };
    return statusConfig[status] || statusConfig.available;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading properties...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Home className="h-8 w-8 text-blue-600" />
              <div>
                <h1 className="text-xl font-bold text-gray-900">{businessInfo.name}</h1>
                <p className="text-xs text-gray-600 italic">{businessInfo.tagline}</p>
              </div>
            </div>
            <Button onClick={() => navigate('/')} variant="outline">
              Back to Home
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            Property Listings in <span className="text-blue-600">Sector Chi V</span>
          </h1>
          <p className="text-lg text-gray-600">
            Discover your dream property from our curated selection
          </p>
        </div>

        {/* Listing-type pills */}
        <div className="mb-4 flex gap-2 flex-wrap">
          {[
            { value: 'all', label: 'All Listings' },
            { value: 'sale', label: 'For Sale' },
            { value: 'rent', label: 'For Rent' },
          ].map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setListingType(opt.value)}
              className={`px-4 py-2 rounded-full border text-sm font-medium transition-colors ${
                listingTypeFilter === opt.value
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
              data-testid={`listing-pill-${opt.value}`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Filters */}
        <Card className="mb-8">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by name, location, type, or BHK..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full md:w-48">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="available">Available</SelectItem>
                  <SelectItem value="under_construction">Under Construction</SelectItem>
                  <SelectItem value="sold">Sold</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Properties Grid */}
        {filteredProperties.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Home className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No properties found</p>
              <p className="text-gray-400 text-sm mt-2">Try adjusting your search or filters</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="mb-4 text-sm text-gray-600">
              Showing {filteredProperties.length} {filteredProperties.length === 1 ? 'property' : 'properties'}
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProperties.map((property) => {
                const statusConfig = getStatusBadge(property.status);
                return (
                  <Card 
                    key={property.id} 
                    className="overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer group"
                    onClick={() => navigate(`/properties/${property.id}`)}
                  >
                    <div className="relative h-64 overflow-hidden">
                      <img
                        src={property.images[0] || 'https://images.unsplash.com/photo-1582407947304-fd86f028f716?w=500'}
                        alt={property.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute top-4 right-4 flex flex-col gap-2 items-end">
                        <Badge className={property.listingType === 'rent' ? 'bg-blue-600 text-white' : 'bg-green-600 text-white'}>
                          {property.listingType === 'rent' ? 'For Rent' : 'For Sale'}
                        </Badge>
                        <Badge className={statusConfig.color}>
                          {statusConfig.label}
                        </Badge>
                      </div>
                      {property.featured && (
                        <div className="absolute top-4 left-4">
                          <Badge className="bg-amber-500 text-white">Featured</Badge>
                        </div>
                      )}
                    </div>
                    <CardContent className="p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                        {property.name}
                      </h3>
                      
                      <div className="flex items-center text-gray-600 mb-4">
                        <MapPin className="h-4 w-4 mr-1" />
                        <span className="text-sm">{property.location}</span>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                        {property.bhk && (
                          <div className="flex items-center text-gray-700">
                            <BedDouble className="h-4 w-4 mr-2 text-blue-600" />
                            <span className="font-medium">{property.bhk}</span>
                          </div>
                        )}
                        <div className="flex items-center text-gray-700">
                          <Maximize className="h-4 w-4 mr-2 text-blue-600" />
                          <span className="font-medium">{property.area}</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-4 border-t">
                        <div>
                          <p className="text-xs text-gray-500">{property.listingType === 'rent' ? 'Monthly Rent' : 'Price'}</p>
                          <div className="flex items-baseline">
                            <IndianRupee className="h-5 w-5 text-blue-600" />
                            <p className="text-xl font-bold text-blue-600">{property.price}</p>
                            {property.listingType === 'rent' && (
                              <span className="text-sm text-gray-500 ml-1">/month</span>
                            )}
                          </div>
                        </div>
                        <Button size="sm" className="bg-blue-600 hover:bg-blue-700">
                          View Details
                          <ArrowRight className="h-4 w-4 ml-2" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Footer CTA */}
      <div className="bg-blue-600 text-white py-12 mt-12">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            Didn't Find What You're Looking For?
          </h2>
          <p className="text-blue-100 mb-6">
            Contact us to discuss your property requirements
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              variant="outline" 
              className="bg-white text-blue-600 hover:bg-gray-100"
              onClick={() => window.location.href = `tel:${businessInfo.phone}`}
            >
              Call Now: {businessInfo.phone}
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="bg-white text-blue-600 hover:bg-gray-100"
              onClick={() => navigate('/#contact')}
            >
              Submit Inquiry
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyListing;
